import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { teamMembers, honors, clients, faqs } from '@/mocks/company';

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qqrfue3oq4jqumtp70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('breadcrumbHome'), item: 'https://www.guodongmedia.com' },
          { '@type': 'ListItem', position: 2, name: t('aboutTitle'), item: 'https://www.guodongmedia.com/about' },
        ],
      },
      {
        '@type': 'AboutPage',
        name: `关于果冻传媒 - ${t('siteName')}`,
        description: '果冻传媒品牌介绍、荣誉资质及联系方式。果冻传媒是专注创意内容生产与品牌传播的专业机构。',
        url: 'https://www.guodongmedia.com/about',
        isPartOf: { '@type': 'WebSite', name: '果冻传媒' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${t('aboutTitle')} - ${t('siteName')}`}
        description="果冻传媒品牌介绍、荣誉资质及联系方式。果冻传媒是专注创意内容生产与品牌传播的专业机构，提供短视频制作、社交媒体运营、广告策划及全案营销服务。"
        keywords="果冻传媒,关于果冻传媒,果冻传媒联系方式,果冻传媒荣誉资质"
        canonicalUrl="https://www.guodongmedia.com/about"
        schema={aboutSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <div className="bg-background-100">
          <Breadcrumb items={[{ label: t('aboutTitle') }]} />

          {/* Brand Intro */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-14">
                <div className="flex-1">
                  <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-6">{t('aboutIntro')}</h1>
                  <div className="space-y-4">
                    <p className="text-base text-foreground-700 leading-relaxed">
                      <strong>果冻传媒</strong>成立于2012年，是一家专注创意内容生产与品牌传播的专业机构。我们以"用创意让品牌发光"为使命，致力于为品牌提供从策略到执行的一站式创意内容解决方案。
                    </p>
                    <p className="text-base text-foreground-700 leading-relaxed">
                      经过十余年的深耕发展，果冻传媒已建立起覆盖<strong>短视频制作</strong>、<strong>品牌营销</strong>、<strong>广告策划</strong>、<strong>社交媒体运营</strong>、<strong>内容创意</strong>及<strong>全案整合营销</strong>的完整服务体系。
                    </p>
                    <p className="text-base text-foreground-700 leading-relaxed">
                      我们的创意团队汇聚了来自4A广告公司、头部MCN机构和互联网大厂的顶尖人才，以深厚的行业洞察和卓越的创意能力，已累计服务超过<strong>500家</strong>品牌客户，打造<strong>2000+</strong>成功案例。
                    </p>
                    <p className="text-base text-foreground-700 leading-relaxed">
                      果冻传媒始终秉持<strong>"创意驱动增长"</strong>的核心理念，在每个项目中追求创意与效果的完美统一。我们相信，好的内容不仅能打动人，更能驱动品牌持续增长。
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 w-full lg:w-[420px] h-[340px] rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20agency%20office%20interior%20with%20team%20brainstorming%2C%20warm%20coral%20and%20gold%20accent%20decor%2C%20collaborative%20workspace%20with%20glass%20walls%20and%20creative%20atmosphere%2C%20professional%20editorial%20photography&width=800&height=680&seq=about-brand-intro&orientation=landscape"
                    alt="果冻传媒办公环境"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Team */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12 md:mb-16">
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">核心团队</h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">顶尖创意人才汇聚，为每一个项目注入专业力量</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {teamMembers.map((member, i) => (
                  <div key={i} className="text-center">
                    <div className="w-36 h-36 md:w-40 md:h-40 rounded-full overflow-hidden mx-auto mb-4">
                      <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-1">{member.name}</h3>
                    <p className="text-sm text-primary-600 font-medium mb-3">{member.role}</p>
                    <p className="text-sm text-foreground-600 leading-relaxed px-2">{member.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full py-14 md:py-20 bg-primary-500">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center">
                <div>
                  <div className="text-3xl md:text-5xl font-heading font-bold text-white mb-2">500+</div>
                  <div className="text-sm md:text-base text-white/70">服务客户</div>
                </div>
                <div>
                  <div className="text-3xl md:text-5xl font-heading font-bold text-white mb-2">2000+</div>
                  <div className="text-sm md:text-base text-white/70">成功案例</div>
                </div>
                <div>
                  <div className="text-3xl md:text-5xl font-heading font-bold text-white mb-2">12年</div>
                  <div className="text-sm md:text-base text-white/70">行业经验</div>
                </div>
                <div>
                  <div className="text-3xl md:text-5xl font-heading font-bold text-white mb-2">80+</div>
                  <div className="text-sm md:text-base text-white/70">专业团队</div>
                </div>
              </div>
            </div>
          </section>

          {/* Honors */}
          <section id="honor" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12 md:mb-16">
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">{t('aboutHonor')}</h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">用专业实力赢得行业认可</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {honors.map((honor, i) => (
                  <div key={i} className="flex items-center gap-4 bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className={`${honor.icon} text-xl`}></i>
                    </div>
                    <span className="text-sm md:text-base font-medium text-foreground-900">{honor.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Clients */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12 md:mb-16">
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">合作品牌</h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">与500+知名品牌共同成长</p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
                {clients.map((client, i) => (
                  <span
                    key={i}
                    className="px-5 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm font-medium text-foreground-700 hover:border-primary-200 transition-colors"
                  >
                    {client}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">{t('aboutContact')}</h2>
                <p className="text-sm md:text-base text-foreground-600">留下您的需求，我们将在24小时内与您联系</p>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
                <form onSubmit={handleSubmit} data-readdy-form>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">{t('name')} <span className="text-primary-600">*</span></label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-foreground-800 mb-1.5">{t('company')}</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的公司名称"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">{t('email')} <span className="text-primary-600">*</span></label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground-800 mb-1.5">{t('phone')}</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的电话"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">{t('message')} <span className="text-primary-600">*</span></label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请描述您的需求，我们将为您匹配最合适的服务方案"
                    ></textarea>
                    <p className="text-xs text-foreground-500 mt-1">最多500字</p>
                  </div>

                  {/* Anti-spam honeypot */}
                  <input
                    type="text"
                    name="phone_alt"
                    className="hp-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {formStatus === 'error' && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                      {formError || '提交失败，请稍后重试'}
                    </div>
                  )}
                  {formStatus === 'success' && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                      感谢您的留言！我们将在24小时内与您联系。
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? '提交中...' : t('submit')}
                  </button>
                </form>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
                <div className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500">电话</div>
                    <div className="text-sm font-medium text-foreground-900">{t('footerPhone')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500">邮箱</div>
                    <div className="text-sm font-medium text-foreground-900">{t('footerEmail')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500">地址</div>
                    <div className="text-sm font-medium text-foreground-900">北京市朝阳区建国路88号</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">常见问题</h2>
                <p className="text-sm md:text-base text-foreground-600">关于我们的服务，您可能想了解</p>
              </div>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <FAQItem key={i} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
      >
        <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{question}</span>
        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-500">
          {open ? (
            <i className="ri-arrow-up-s-line text-lg"></i>
          ) : (
            <i className="ri-arrow-down-s-line text-lg"></i>
          )}
        </span>
      </button>
      {open && (
        <div className="px-4 md:px-5 pb-4 md:pb-5">
          <p className="text-sm text-foreground-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}